import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Star, MessageSquare, Phone, Mail, Lock, EyeOff, RotateCcw, ImageIcon
} from 'lucide-react';

// Formatador de data/hora com tag de turno automatica
const formatFeedbackTime = (isoDate) => {
  if (!isoDate) return { full: '', relative: '', shift: '', shiftIcon: '' };
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const hour = date.getHours();

  let shift = 'Tarde', shiftIcon = '☀️';
  if (hour >= 6 && hour < 12) { shift = 'Manha'; shiftIcon = '🌅'; }
  else if (hour >= 12 && hour < 18) { shift = 'Tarde'; shiftIcon = '☀️'; }
  else if (hour >= 18 && hour < 23) { shift = 'Noite'; shiftIcon = '🌙'; }
  else { shift = 'Madrugada'; shiftIcon = '🌃'; }

  const full = date.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  let relative = '';
  if (diffMs >= 0 && diffMs < 24 * 60 * 60 * 1000) {
    if (diffMin < 1) relative = 'agora';
    else if (diffMin < 60) relative = `ha ${diffMin} min`;
    else relative = `ha ${diffH}h`;
  }

  return { full, relative, shift, shiftIcon };
};

export const DashboardFeedbackFeed = ({
  feedbacks, hiddenCount, showArchived, getFeedbackTypeName,
  openWhatsApp, openEmail, archiveFeedback,
  planUsage, setShowWhatsAppLock, setShowArchiveLock, setViewingPhoto,
  theme, canArchive
}) => {
  const { dm, cardBg, textPrimary, textSecondary, textMuted } = theme;

  return (
    <Card className={`lg:col-span-7 rounded-xl border shadow-sm ${cardBg} transition-colors duration-300`} data-testid="feedback-feed">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className={`text-base font-semibold ${textPrimary}`}>
            {showArchived ? 'Avaliacoes Ocultas' : 'Avaliacoes Recentes'}
          </CardTitle>
          <span className={`text-sm ${textSecondary}`}>{feedbacks.length} total</span>
        </div>
        {showArchived && (
          <div className={`mt-2 px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${dm ? 'bg-[#C5A059]/10 text-[#C5A059]' : 'bg-[#0A2647]/5 text-[#0A2647]'}`} data-testid="archived-mode-banner">
            <EyeOff className="w-3.5 h-3.5" />
            Exibindo avaliacoes ocultas. Clique em <strong>Restaurar</strong> para devolve-las a lista principal.
          </div>
        )}
      </CardHeader>
      <CardContent className="max-h-[450px] overflow-y-auto">
        {hiddenCount > 0 && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="hidden-messages-banner">
            <div className="flex items-center gap-2 flex-1">
              <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Lock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  {hiddenCount} {hiddenCount === 1 ? 'avaliacao bloqueada' : 'avaliacoes bloqueadas'} — seus clientes continuam avaliando!
                </p>
                <p className="text-xs text-amber-600">Seu formulario funciona normalmente para os clientes, mas você esta sem acesso ao conteudo e sem alertas de crise. Faca upgrade para voltar a ver tudo.</p>
              </div>
            </div>
            <Link to="/plans">
              <Button size="sm" className="bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] font-bold text-xs whitespace-nowrap" data-testid="upgrade-banner-btn">
                Fazer Upgrade
              </Button>
            </Link>
          </div>
        )}
        {feedbacks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <MessageSquare className={`w-12 h-12 ${dm ? 'text-slate-600' : 'text-slate-300'} mb-3`} />
            <p className={`${textSecondary} font-medium`}>Nenhuma avaliacao encontrada</p>
            <p className={`text-sm ${textMuted}`}>Ajuste os filtros ou aguarde novas avaliacoes</p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedbacks.map((feedback) => (
              <div
                key={feedback.id}
                className={`card-feedback ${
                  feedback.hidden ? 'opacity-60' :
                  feedback.feedback_type === 'complaint' ? 'card-feedback-complaint' :
                  feedback.feedback_type === 'suggestion' ? 'card-feedback-suggestion' :
                  'card-feedback-praise'
                } ${feedback.is_urgent && !feedback.hidden ? 'urgent-card' : ''}`}
                data-testid={`feedback-card-${feedback.id}`}
              >
                {feedback.hidden ? (
                  <div className="flex items-center gap-3 py-3 px-1">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Lock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-slate-600">
                          {feedback.hidden_reason === 'retencao' ? 'Avaliação esperando por você' : 'Avaliacao bloqueada'}
                        </p>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">
                          {feedback.feedback_type === 'complaint' ? 'Critica' : feedback.feedback_type === 'suggestion' ? 'Sugestao' : 'Elogio'}
                        </span>
                      </div>
                      {feedback.message_preview && (
                        <p className="text-sm text-slate-500 select-none" style={{ filter: 'blur(4px)', WebkitUserSelect: 'none' }}>
                          {feedback.message_preview}
                        </p>
                      )}
                      <p className="text-xs text-amber-600 mt-1.5">
                        {feedback.hidden_reason === 'retencao' ? (
                          <>Regularize seu plano para ver o que seu cliente disse — aparece na hora.{' '}
                          <Link to="/plans" className="text-[#C5A059] font-bold hover:underline">Liberar minhas avaliações</Link></>
                        ) : (
                          <>Conteudo real recebido, mas bloqueado pelo limite do plano.{' '}
                          <Link to="/plans" className="text-[#C5A059] font-bold hover:underline">Fazer Upgrade para Ler</Link></>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 text-xs text-slate-400 flex-shrink-0">
                      {feedback.rating_average != null && (
                        <div className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 text-amber-400" />
                          <span className="font-medium">{feedback.rating_average}</span>
                        </div>
                      )}
                      {(() => {
                        const t = formatFeedbackTime(feedback.created_at);
                        return (
                          <div className="flex flex-col items-end gap-0.5" data-testid={`feedback-time-locked-${feedback.id}`}>
                            <span title={t.full}>{t.relative || t.full}</span>
                            <span className="text-[10px] opacity-75">{t.shiftIcon} {t.shift}</span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`font-semibold ${textPrimary}`}>{feedback.customer_name}</span>
                      <span className={
                        feedback.feedback_type === 'complaint' ? 'badge-complaint' :
                        feedback.feedback_type === 'suggestion' ? 'badge-suggestion' :
                        'badge-praise'
                      }>
                        {getFeedbackTypeName(feedback.feedback_type)}
                      </span>
                      {feedback.is_urgent && <span className="badge-urgent">URGENTE</span>}
                      {feedback.responded && <span className="badge-responded">Respondido</span>}
                    </div>
                    <p className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-600'} whitespace-pre-wrap break-words mb-3`}>{feedback.message}</p>
                    {feedback.complaint_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`feedback-tags-${feedback.id}`}>
                        {feedback.complaint_tags.map(tag => (
                          <span key={tag} className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-100 text-red-700">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {(feedback.photo_urls?.length > 0 || feedback.photo_url) && (
                      <div className="mb-3 flex flex-wrap gap-2">
                        {(feedback.photo_urls || (feedback.photo_url ? [feedback.photo_url] : [])).map((url, idx) => {
                          const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                          return (
                            <button
                              key={url}
                              type="button"
                              onClick={() => setViewingPhoto(fullUrl)}
                              className={`flex items-center gap-2 rounded-lg overflow-hidden border ${dm ? 'border-slate-600 hover:border-[#C5A059]' : 'border-slate-200 hover:border-[#C5A059]'} transition-all group cursor-pointer relative`}
                              data-testid={`feedback-photo-thumb-${feedback.id}-${idx}`}
                            >
                              <img
                                src={fullUrl}
                                alt={`Foto ${idx + 1}`}
                                className="w-16 h-16 object-cover flex-shrink-0"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const fallback = e.currentTarget.nextElementSibling;
                                  if (fallback) fallback.classList.remove('hidden');
                                }}
                              />
                              <div className={`hidden w-16 h-16 flex-shrink-0 items-center justify-center ${dm ? 'bg-slate-800 text-slate-500' : 'bg-slate-100 text-slate-400'} text-[9px] text-center px-1`} data-testid={`feedback-photo-error-${feedback.id}-${idx}`}>
                                Foto<br/>indisponível
                              </div>
                              {(feedback.photo_urls || []).length <= 1 && (
                                <div className="px-3 py-2">
                                  <span className={`text-xs font-medium ${dm ? 'text-[#C5A059] group-hover:text-[#d4af61]' : 'text-[#0A2647] group-hover:text-[#C5A059]'} flex items-center gap-1`}>
                                    <ImageIcon className="w-3.5 h-3.5" />Ver foto anexada
                                  </span>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${textMuted}`}>
                      <span className="flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 text-[#C5A059] fill-[#C5A059]" />{feedback.rating_average} media
                      </span>
                      {feedback.rating_food != null && (
                        <span className={`flex items-center gap-1 ${dm ? 'text-slate-400' : 'text-slate-500'}`} data-testid={`rating-food-${feedback.id}`}>
                          <span className="text-[10px] font-semibold uppercase tracking-wide">Produtos</span>
                          <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-400 fill-amber-400" />{feedback.rating_food}</span>
                        </span>
                      )}
                      {feedback.rating_service != null && (
                        <span className={`flex items-center gap-1 ${dm ? 'text-slate-400' : 'text-slate-500'}`} data-testid={`rating-service-${feedback.id}`}>
                          <span className="text-[10px] font-semibold uppercase tracking-wide">Atendimento</span>
                          <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-400 fill-amber-400" />{feedback.rating_service}</span>
                        </span>
                      )}
                      {feedback.rating_ambiance != null && (
                        <span className={`flex items-center gap-1 ${dm ? 'text-slate-400' : 'text-slate-500'}`} data-testid={`rating-ambiance-${feedback.id}`}>
                          <span className="text-[10px] font-semibold uppercase tracking-wide">Ambiente</span>
                          <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-400 fill-amber-400" />{feedback.rating_ambiance}</span>
                        </span>
                      )}
                      {(() => {
                        const t = formatFeedbackTime(feedback.created_at);
                        return (
                          <span className="flex items-center gap-1.5" data-testid={`feedback-time-${feedback.id}`} title={t.full}>
                            <span>{t.relative || t.full}</span>
                            <span className="text-[10px] opacity-75 whitespace-nowrap">· {t.shiftIcon} {t.shift}</span>
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <Button size="sm" className="whatsapp-btn h-8 text-xs" onClick={() => openWhatsApp(feedback)} data-testid={`respond-whatsapp-${feedback.id}`}>
                      <Phone className="w-3.5 h-3.5" />WhatsApp
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openEmail(feedback)} data-testid={`respond-email-${feedback.id}`}>
                      <Mail className="w-3.5 h-3.5 mr-1" />E-mail
                    </Button>
                    {canArchive ? (
                      showArchived ? (
                        <Button size="sm" variant="outline" className={`h-8 text-xs gap-1 ${dm ? 'border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10' : 'border-emerald-300 text-emerald-600 hover:bg-emerald-50'}`} onClick={() => archiveFeedback(feedback.id)} data-testid={`restore-feedback-${feedback.id}`}>
                          <RotateCcw className="w-3.5 h-3.5" />Restaurar
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className={`h-8 text-xs gap-1 ${dm ? 'border-slate-600 text-slate-400 hover:text-red-400 hover:border-red-400/50' : 'border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-300'}`} onClick={() => archiveFeedback(feedback.id)} data-testid={`archive-feedback-${feedback.id}`}>
                          <EyeOff className="w-3.5 h-3.5" />Ocultar
                        </Button>
                      )
                    ) : (
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1 border-slate-300 text-slate-400" onClick={() => setShowArchiveLock(true)} data-testid={`archive-feedback-locked-${feedback.id}`}>
                        <Lock className="w-3 h-3" /><EyeOff className="w-3.5 h-3.5" />Ocultar
                      </Button>
                    )}
                  </div>
                </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
